import apiClient from './apiClient';

/**
 * Cache für Anlage-V-Daten mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * Pattern analog zu propertyCacheService.js:
 * - TTL 15min (Anlage-V-Berechnungen sind teuer; manueller Refresh via #steuer-refresh-button)
 * - Explizite Invalidierung nach Mutationen (ObjectCost, Loan, RentalContract)
 * - ETag-Support (304 Not Modified)
 * - localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 *
 * WICHTIG: Importiert nur apiClient — kein Import von anderen Services,
 * um Importzyklen zu vermeiden. Andere Services importieren dieses Modul.
 */
const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `anlage-v:${CACHE_VERSION}:`;
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 Minuten

// In-Memory Cache als Fallback (Private Mode, localStorage nicht verfügbar)
const memoryCache = new Map();
const pendingRequests = new Map();

// Invalidation-Token für Race-Condition-Schutz bei clearCache()
let cacheInvalidationToken = 0;

/**
 * Prüft ob localStorage verfügbar ist.
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__anlage_v_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * Liest Cache-Eintrag aus localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @returns {object|null} Cache-Eintrag oder null wenn nicht vorhanden/ungültig
 */
const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Schreibt Cache-Eintrag in localStorage.
 * @param {string} key - Cache-Key (ohne Prefix)
 * @param {object} value - Cache-Wert { data, timestamp, etag }
 */
const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

/**
 * Gibt eine flache Kopie des gecachten DTOs zurück,
 * damit Aufrufer den Cache-Eintrag nicht mutieren können.
 * @param {object} data - Gecachtes DTO
 * @returns {object} Flache Kopie mit kopiertem lines-Array
 */
const cloneDto = (data) => {
  if (!data || typeof data !== 'object') return data;
  return { ...data, lines: Array.isArray(data.lines) ? [...data.lines] : data.lines };
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

/**
 * Löscht alle Cache-Einträge aus localStorage.
 */
const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests auf /api/anlage-v-Endpoints.
 * Unterstützt localStorage-Persistenz und ETag-basierte Revalidierung.
 * @param {string} url - API-Pfad
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<object>} AnlageVDTO
 */
const getCached = (url, params, signal, _retryCount = 0) => {
  // Frühzeitig abbrechen wenn Signal bereits abgebrochen
  if (signal?.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(cloneDto(memoryCached.data));
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(cloneDto(localCached.data));
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;
  if (signal) config.signal = signal;

  // ETag senden falls vorhanden (Revalidierung)
  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  // Promise-Referenz merken, damit im finally nur dann aus
  // pendingRequests gelöscht wird, wenn es noch unser eigener Eintrag ist.
  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn clearCache()/invalidate aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cloneDto(cachedData);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return cloneDto(response.data);
      }
      return cloneDto(response.data);
    }

    if (response.status === 304) {
      // 304 Not Modified: Cached Daten nutzen, Timestamp aktualisieren
      if (!staleEntry) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return null;
      }

      // Abbruch-Schutz: nicht cachen wenn Signal abgebrochen
      if (signal?.aborted) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        return cloneDto(staleEntry.data);
      }

      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return cloneDto(staleEntry.data);
    }

    // Abbruch-Schutz: nicht cachen wenn Signal abgebrochen (200 OK)
    if (signal?.aborted) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return cloneDto(response.data);
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    return cloneDto(data);
  }).catch(err => {
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    // Axios-Abbruch (CanceledError / ERR_CANCELED) sauber behandeln: kein Cache vergiften
    if (err && (err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) {
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      throw abortError;
    }
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(promise, signal);
};

const anlageVCacheService = {
  /**
   * Lädt oder berechnet den Anlage-V-Datensatz (gecacht, 15min TTL).
   *
   * @param {'PROPERTY_GROUP'|'PROPERTY'} scope - Scope der Anlage V
   * @param {string} targetId - UUID der Liegenschaft (PROPERTY_GROUP) oder nicht-zugeordneten Mieteinheit (PROPERTY)
   * @param {number|string} year - Steuerjahr
   * @param {AbortSignal} [signal] - AbortController-Signal
   * @returns {Promise<object>} AnlageVDTO inkl. scope, propertyGroupId/propertyId, lines[], etc.
   */
  getAnlageV: (scope, targetId, year, signal) => {
    const params = scope === 'PROPERTY_GROUP'
      ? { propertyGroupId: targetId, year }
      : { propertyId: targetId, year };
    return getCached('/anlage-v', params, signal);
  },

  /**
   * Speichert manuelle Überschreibungen für bearbeitbare Zeilen.
   * Invalidiert danach den gesamten Cache.
   * @param {string} id - UUID des AnlageVDTO
   * @param {{ overrides: Array<{ lineCode: string, overrideValue: number|null, manual: boolean }> }} request
   * @returns {Promise<object>} Aktualisiertes AnlageVDTO
   */
  saveOverrides: async (id, request) => {
    const response = await apiClient.put(`/anlage-v/${id}`, request);
    anlageVCacheService.invalidateAll();
    return response.data;
  },

  /**
   * Lädt das Anlage-V-PDF als Blob-Antwort. Nicht gecacht.
   * Liefert die rohe Axios-Response zurück, damit die Seite sowohl
   * den Blob (200) als auch den 503-Fehler-Text auswerten kann.
   *
   * @param {string} id - UUID des AnlageVDTO
   * @returns {Promise<import('axios').AxiosResponse>} Axios-Response mit Blob-Daten
   */
  downloadPdf: (id) =>
    apiClient.get(`/anlage-v/${id}/pdf`, {
      responseType: 'blob',
      validateStatus: (status) => status === 200
    }),

  /**
   * Invalidiert alle Anlage-V-Caches (nach Mutationen der Datenquellen).
   * Wird aufgerufen von: objektkostenService, loanCacheService, ContractWizard, ContractsPage.
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default anlageVCacheService;
